import { Visibility, getPath, isRecord, itemVisibility } from './cardModel';
import { FormField, FormOption } from './cardTypes';

export type OptionLabels = Record<string, Record<string, string>>;

export const NOT_IN_LIST_TEXT = 'Not in the list (it may have been removed)';

export const WEEK_DAYS: FormOption[] = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

export const dayLabel = (day: string): string =>
  WEEK_DAYS.find(option => option.value === day)?.label ?? day;

export const toList = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [value];

export const scalar = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value) ?? '';
};

const textOf = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const labelFor = (
  field: FormField,
  item: string,
  labels: OptionLabels,
): string =>
  field.options?.find(option => option.value === item)?.label ??
  labels[field.key]?.[item] ??
  item;

export const isBlank = (value: unknown): boolean =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

const RECORD_ID = 'id';
const RECORD_NAME = 'name';

const recordNameKey = (key: string): string | undefined => {
  if (key === RECORD_ID) {
    return RECORD_NAME;
  }
  const suffix = `.${RECORD_ID}`;
  return key.endsWith(suffix)
    ? `${key.slice(0, -suffix.length)}.${RECORD_NAME}`
    : undefined;
};

export const ownLabels = (
  fields: FormField[],
  prefill: Record<string, unknown> | undefined,
): OptionLabels => {
  const values = prefill ?? {};
  return Object.fromEntries(
    fields
      .filter(field => field.readOnly === true && field.target === true)
      .flatMap((field): Array<[string, Record<string, string>]> => {
        const nameKey = recordNameKey(field.key);
        const name = nameKey === undefined ? '' : getPath(values, nameKey);
        const stored = getPath(values, field.key);
        if (
          typeof name !== 'string' ||
          name.trim() === '' ||
          isBlank(stored) ||
          Array.isArray(stored)
        ) {
          return [];
        }
        return [[field.key, { [scalar(stored)]: name }]];
      }),
  );
};

export const displayLines = (
  field: FormField,
  value: unknown,
  labels: OptionLabels,
  visibility: Visibility,
): string[] => {
  if (field.type === 'toggle') {
    return [value === true ? 'Yes' : 'No'];
  }
  if (isBlank(value)) {
    return [];
  }
  switch (field.type) {
    case 'durationMinutes':
      return [`${scalar(value)} min`];
    case 'select':
    case 'multiselect':
    case 'userPicker':
    case 'entityPicker':
      return [
        toList(value)
          .map(item => labelFor(field, scalar(item), labels))
          .join(', '),
      ];
    case 'numberList':
      return [toList(value).map(scalar).join(', ')];
    case 'weeklyHours':
      return toList(value)
        .filter(isRecord)
        .map(row => {
          const day = dayLabel(textOf(row.day));
          return `${day} ${textOf(row.start)}–${textOf(row.end)}`;
        });
    case 'repeater':
      return toList(value).map(item => {
        const record = isRecord(item) ? item : {};
        const itemFields = field.itemFields ?? [];
        const inItem = itemVisibility(itemFields, record, visibility);
        return itemFields
          .filter(
            itemField => itemField.type !== 'note' && inItem.field(itemField),
          )
          .map(itemField => {
            const shown = displayLines(
              itemField,
              getPath(record, itemField.key),
              labels,
              inItem,
            ).join(', ');
            return `${itemField.label}: ${shown === '' ? 'Not set' : shown}`;
          })
          .join(' · ');
      });
    default:
      return [scalar(value)];
  }
};
